import { getSDKFeatureDecision, SDK_FEATURES } from '../feature-flags/sdk-feature-decision'

export type InlineGridPath = 'legacy' | 'iframe'

export type InlineGridDecisionSource =
  | 'remote_flag'
  | 'provider_fallback'
  | 'invalid_response'
  | 'no_inline_grid'
  | 'renderer_unavailable'

export interface InlineGridDecision {
  readonly path: InlineGridPath
  readonly source: InlineGridDecisionSource
}

type RemoteInlineGridDecision = 'on' | 'off'

interface ResolveInlineGridDecisionProps {
  featureFlagSets: readonly unknown[]
}

export const makeInlineGridDecision = (
  path: InlineGridPath,
  source: InlineGridDecisionSource
): InlineGridDecision => Object.freeze({ path, source })

export const DEFAULT_INLINE_GRID_DECISION = makeInlineGridDecision('legacy', 'no_inline_grid')

const getInlineGridRemoteDecisions = (
  featureFlagSets: readonly unknown[]
): Array<RemoteInlineGridDecision | null | 'invalid'> =>
  featureFlagSets.map((featureFlags) =>
    getSDKFeatureDecision(featureFlags, SDK_FEATURES.inlineGridIframe)
  )

/**
 * Reduces every inline-grid flag result on the page to one immutable initial render decision.
 * Flag evaluation never starts a page with mixed renderers: any off, missing, invalid, or mixed
 * decision selects legacy for every grid. After an iframe decision, profile, URL, setup, and
 * runtime failures may fall back only the affected grid.
 */
export const resolveInlineGridDecision = ({
  featureFlagSets,
}: ResolveInlineGridDecisionProps): InlineGridDecision => {
  const remoteDecisions = getInlineGridRemoteDecisions(featureFlagSets)
  let sdkDecision: InlineGridDecision

  if (!remoteDecisions.length) {
    sdkDecision = DEFAULT_INLINE_GRID_DECISION
  } else if (remoteDecisions.some((decision) => decision === 'invalid')) {
    sdkDecision = makeInlineGridDecision('legacy', 'invalid_response')
  } else if (remoteDecisions.some((decision) => decision === null)) {
    sdkDecision = makeInlineGridDecision('legacy', 'provider_fallback')
  } else {
    const shouldRenderIframe = remoteDecisions.every((decision) => decision === 'on')

    sdkDecision = makeInlineGridDecision(shouldRenderIframe ? 'iframe' : 'legacy', 'remote_flag')
  }

  return sdkDecision
}
